import { useCallback, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components/image/src/image";
import { PlayPauseControls } from "@/libraries/design/components/play-pause-controls/src/play-pause-controls";
import { wacToken, ColumnLayout } from "@/libraries/design/foundations";
import { useWacRefresh } from "@/utils/wac-refresh/wac-refresh-context";

export interface ConnectorLogo {
    src: string;
    alt: string;
    href?: string;
}

export interface MarqueeProps {
    mobileLogos?: ConnectorLogo[];
    tabletLogos?: ConnectorLogo[];
    desktopLogos: ConnectorLogo[];
}

export interface LogoProps extends ConnectorLogo {
    ariaHidden: boolean;
    tabIndex: 0 | -1;
}

const Logo = ({
    src,
    href,
    alt,
    ariaHidden,
    tabIndex,
    isWacRefresh
}: LogoProps & { isWacRefresh?: boolean }) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = useCallback(() => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: "connectorLogo",
            source: "connector",
            attributes: { label: alt }
        }).fire();
    }, [createAnalyticsEvent, alt]);

    return (
        <li aria-hidden={ariaHidden}>
            <a
                href={href}
                tabIndex={tabIndex}
                onClick={handleClick}
                css={[
                    linkCss,
                    isWacRefresh ? refreshLinkCss : nonRefreshLinkHoverCss
                ]}
            >
                <div css={[logoCss, isWacRefresh && refreshLogoCss]}>
                    <Image
                        src={src}
                        alt={alt}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain"
                        }}
                    />
                </div>
            </a>
        </li>
    );
};

export const LogosMarquee = ({
    mobileLogos,
    tabletLogos,
    desktopLogos
}: // eslint-disable-next-line complexity
MarqueeProps) => {
    const isWacRefresh = useWacRefresh();

    // Default to true (running); correct to false after mount if the user prefers reduced motion.
    // We do this in useEffect (not useState initializer) to avoid SSR/hydration mismatches,
    // since window.matchMedia is not available on the server.
    const [running, setRunning] = useState(true);
    useEffect(() => {
        if (
            typeof window !== "undefined" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            setRunning(false);
        }
    }, []);

    tabletLogos = tabletLogos?.length === 0 ? desktopLogos : tabletLogos;
    mobileLogos = mobileLogos?.length === 0 ? desktopLogos : mobileLogos;

    // @ts-expect-error Added to prevent undefined type errors
    const mobileLogosLength = mobileLogos.length;
    // @ts-expect-error Added to prevent undefined type errors
    const tabletLogosLength = tabletLogos.length;
    const desktopLogosLength = desktopLogos.length;

    mobileLogos = [
        // @ts-expect-error Added to prevent undefined type errors
        ...mobileLogos,
        ...mobileLogos,
        ...mobileLogos,
        ...mobileLogos
    ];
    tabletLogos = [
        ...tabletLogos,
        ...tabletLogos,
        ...tabletLogos,
        ...tabletLogos
    ];
    desktopLogos = [
        ...desktopLogos,
        ...desktopLogos,
        ...desktopLogos,
        ...desktopLogos
    ];

    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handlePlayPauseClick = useCallback(() => {
        const next = !running;
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "marqueePlayPause",
            source: "connector",
            attributes: { label: next ? "play" : "pause" }
        }).fire();
        setRunning(next);
    }, [running, createAnalyticsEvent]);

    const pauseLabel = intl.formatMessage({
        id: "connector-logo-marquee.pause",
        defaultMessage: "Pause"
    });

    const playLabel = intl.formatMessage({
        id: "connector-logo-marquee.play",
        defaultMessage: "Play"
    });

    const playPauseButton = (
        <ColumnLayout layout="12">
            <div css={marqueeButtonContainerCss}>
                {isWacRefresh ? (
                    <PlayPauseControls
                        isPlaying={running}
                        onToggle={() => setRunning((r) => !r)}
                        mediaLabel={intl.formatMessage({
                            id: "connector-logo-marquee.media-label",
                            defaultMessage: "connector logos"
                        })}
                    />
                ) : (
                    <button
                        type="button"
                        css={marqueeButtonCss}
                        onClick={handlePlayPauseClick}
                        aria-pressed={running}
                        aria-label={running ? pauseLabel : playLabel}
                    >
                        <div css={buttonCirlceCss}>
                            {running ? (
                                <VideoPauseOverlayIcon label={pauseLabel} />
                            ) : (
                                <VideoPlayOverlayIcon label={playLabel} />
                            )}
                        </div>
                    </button>
                )}
            </div>
        </ColumnLayout>
    );

    const logosList = (
        <div css={listContainerCss}>
            <ul
                css={[
                    animationKeyframesCss,
                    listCss,
                    css({
                        animation: "scroll 18s linear infinite",
                        animationPlayState: running ? "running" : "paused"
                    }),
                    pauseOnHoverCss,
                    mobileCss,
                    isWacRefresh && refreshListPaddingCss
                ]}
            >
                {mobileLogos.map((logo, index) => {
                    const isOriginal = index < mobileLogosLength;
                    return (
                        <Logo
                            key={index}
                            ariaHidden={!isOriginal}
                            tabIndex={isOriginal ? 0 : -1}
                            isWacRefresh={isWacRefresh}
                            {...logo}
                        />
                    );
                })}
            </ul>
            <ul
                css={[
                    animationKeyframesCss,
                    listCss,
                    css({
                        animation: "scroll 36s linear infinite",
                        animationPlayState: running ? "running" : "paused"
                    }),
                    pauseOnHoverCss,
                    tabletCss,
                    isWacRefresh && refreshListPaddingCss
                ]}
            >
                {tabletLogos.map((logo, index) => {
                    const isOriginal = index < tabletLogosLength;
                    return (
                        <Logo
                            key={index}
                            ariaHidden={!isOriginal}
                            tabIndex={isOriginal ? 0 : -1}
                            isWacRefresh={isWacRefresh}
                            {...logo}
                        />
                    );
                })}
            </ul>
            <ul
                css={[
                    animationKeyframesCss,
                    listCss,
                    css({
                        animation: "scroll 90s linear infinite",
                        animationPlayState: running ? "running" : "paused"
                    }),
                    pauseOnHoverCss,
                    desktopCss,
                    isWacRefresh && refreshListPaddingCss
                ]}
            >
                {desktopLogos.map((logo, index) => {
                    const isOriginal = index < desktopLogosLength;
                    return (
                        <Logo
                            key={index}
                            ariaHidden={!isOriginal}
                            tabIndex={isOriginal ? 0 : -1}
                            isWacRefresh={isWacRefresh}
                            {...logo}
                        />
                    );
                })}
            </ul>
        </div>
    );

    return (
        <div css={marqueeContainerCss}>
            {logosList}
            {playPauseButton}
        </div>
    );
};

const linkCss = css({
    display: "flex",
    backgroundColor: wacToken("color.neutral.dark100"),
    borderRadius: wacToken("corner.radius.card"),
    justifyContent: "center",
    alignItems: "center",

    [media.below.sm]: {
        height: "80px",
        width: "80px"
    }
});

const nonRefreshLinkHoverCss = css({
    "&:hover": {
        backgroundColor: wacToken("color.neutral.dark200")
    }
});

const refreshLinkCss = css({
    "backgroundColor": wacToken("color.neutral.light0"),
    "borderRadius": "24px",
    // show the top border of the logo
    "marginTop": "1px",

    "&:hover": {
        boxShadow: token("elevation.shadow.overlay")
    }
});

const logoCss = css({
    height: "60px",
    width: "60px",
    margin: "10px"
});

const refreshLogoCss = css({
    height: "75px",
    width: "75px",
    aspectRatio: "1",
    margin: "12.5px",

    [media.below.md]: {
        height: "56px",
        width: "56px",
        margin: "12px"
    }
});

const listCss = css({
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    gap: token("space.250"),
    width: "fit-content"
});

const refreshListPaddingCss = css({
    // show the drop shadow of the logo
    paddingBlockStart: token("space.050"),
    paddingBlockEnd: token("space.200")
});

const marqueeContainerCss = css({
    overflowX: "hidden",
    overflowY: "visible",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: token("space.150")
});

const marqueeButtonContainerCss = css({
    width: "100%",
    display: "flex",
    justifyContent: "end",
    paddingBlockEnd: token("space.050")
});

const marqueeButtonCss = css({
    height: "44px",
    width: "44px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "100%",
    overflow: "visible",

    ["&:hover"]: {
        cursor: "pointer"
    }
});

const buttonCirlceCss = css({
    height: "32px",
    width: "32px",
    backgroundColor: wacToken("color.neutral.dark400"),
    border: "none",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    ["&:hover"]: {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.dark500")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        color: `${wacToken("color.neutral.dark1000")} !important`
    }
});

const listContainerCss = css({
    width: "100%",
    maxWidth: "4000px",
    margin: "auto",
    overflowX: "hidden"
});

const animationKeyframesCss = css({
    width: "100%",
    whiteSpace: "nowrap",

    ["@keyframes scroll"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: { transform: "translateX(0%)" },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: { transform: "translateX(calc(-50% - 10px))" }
    }
});

const pauseOnHoverCss = css({
    ["&:hover"]: {
        animationPlayState: "paused"
    }
});

const desktopCss = css({
    [media.below.md]: {
        display: "none"
    }
});

const tabletCss = css({
    [media.above.md]: {
        display: "none"
    },
    [media.below.sm]: {
        display: "none"
    }
});

const mobileCss = css({
    [media.above.sm]: {
        display: "none"
    }
});
